import {Icons} from '@continuum/ui';
import {cva} from 'class-variance-authority';
import {forwardRef} from 'react';
import {Link} from 'react-router-dom';
import type {PageListItem} from './utils';
import {formatSharedAt} from './utils';

interface Props {
  item: PageListItem;
  isCurrent: boolean;
  showAdvisor: boolean;
  onNavigate: () => void;
}

export const pageCardVariants = cva(
  'flex w-full items-center gap-3 rounded-xl border border-border bg-background px-4 py-3.5 text-left sm:px-5 sm:py-4',
  {
    variants: {
      state: {
        link: 'transition-[background-color,border-color,transform] duration-150 hover:border-foreground/20 hover:bg-muted/60 active:scale-[0.99] active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        current: 'bg-muted',
        skeleton: '',
      },
    },
    defaultVariants: {state: 'link'},
  },
);

export const PageRow = forwardRef<HTMLElement, Props>(
  ({item, isCurrent, showAdvisor, onNavigate}, ref) => {
    const date = formatSharedAt(item.sharedAt);

    const body = (
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 break-words text-sm font-medium leading-snug text-foreground sm:text-base">
          {item.title ?? 'Untitled page'}
        </span>
        <span className="mt-1.5 flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
          <Icons.calendar aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate">
            {showAdvisor && item.advisorName
              ? `${date} · ${item.advisorName}`
              : date}
          </span>
        </span>
      </span>
    );

    if (isCurrent) {
      return (
        <span
          ref={ref}
          aria-current="page"
          className={pageCardVariants({state: 'current'})}
        >
          {body}
        </span>
      );
    }

    return (
      <Link
        to={`/${item.identifier}`}
        onClick={onNavigate}
        className={pageCardVariants({state: 'link'})}
      >
        {body}
        <Icons.chevronRight
          aria-hidden
          className="size-5 shrink-0 text-muted-foreground"
        />
      </Link>
    );
  },
);
PageRow.displayName = 'PageRow';
